import { InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { ConfigProvider, Form, Input, Radio, Spin, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { FieldCard } from '@/components/profile/settingCards';
import { cn } from '@/libs/utils';
import { TYPE_OF_EMPLOYEE_OPTIONS } from '@/shared/employee';
import { DATE_FORMATS } from '@/utils/formUtils';
import { formValidateAndSetNumber } from '@/utils/inputApi';
import AdditionalHolidayDaysSection from '../../AdditionalHolidayDaysSection';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';

/**
 * One line of the balance: sign, label (with an optional muted note) and the
 * amount — a number, or the field itself when it is editable.
 */
function LedgerRow({ sign, label, note, children, total }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2',
        total
          ? 'rounded-b-xl border-t-2 border-[rgba(var(--accentColor),0.35)] bg-[rgba(var(--accentColor),0.08)]'
          : 'border-t border-zinc-100 first:border-t-0'
      )}
    >
      <span
        className={cn(
          'w-4 shrink-0 text-center text-[15px] font-bold',
          total ? 'text-accent' : 'text-zinc-400'
        )}
      >
        {sign}
      </span>
      <span className="min-w-0 flex-1 leading-tight">
        <span
          className={cn(
            'block',
            total
              ? 'text-[13.5px] font-extrabold text-accent'
              : 'text-[12.5px] font-semibold text-zinc-800'
          )}
        >
          {label}
        </span>
        {note && (
          <span className="block text-[11.5px] font-normal text-zinc-500">
            {note}
          </span>
        )}
      </span>
      <div
        className={cn(
          // Every figure ends 8px from the edge: a plain number by padding, an
          // input by 1px border + 7px padding in both the view and edit modes
          // (the profile's read-only style drops it otherwise, so the rule
          // needs its specificity).
          // A number input also keeps room for its spin buttons on the
          // right, so they are hidden; typing works the same.
          'w-20 shrink-0 pr-2 text-right has-[input]:pr-0 [&_input.ant-input]:!pr-[7px] [&_input::-webkit-inner-spin-button]:appearance-none [&_input::-webkit-outer-spin-button]:appearance-none [&_input]:text-right [&_input]:[-moz-appearance:textfield]',
          total
            ? 'text-[24px] font-extrabold text-accent'
            : 'text-[15px] font-bold text-zinc-900'
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** A secondary figure under the balance: label left, value right. */
function SideFigure({ label, hint, value, loading }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-[12.5px]">
      <span className="flex-1 text-zinc-600">
        {label}
        {hint && (
          <Tooltip title={hint}>
            <InfoCircleOutlined className="ml-1 text-[11px] text-zinc-400" />
          </Tooltip>
        )}
      </span>
      <span className="text-[15px] font-bold text-zinc-900">
        {loading ? <Spin size="small" /> : (value ?? '—')}
      </span>
    </div>
  );
}

/**
 * The "Annual leave" tile of the employee profile: the balance on top, the
 * day counts as cards, extra days below. Same fields, conditions and save as
 * EmployeeHolidayDaysFields + EmployeeHolidaySummary, which step aside on the
 * profile.
 */
export default function EmployeeHolidayProfilePanel() {
  const {
    calculationData,
    calculationLoading,
    employeeId,
    employeeTrackerTypeKeys,
    employeeTrackerTypeState,
    endDate,
    form,
    isEdited,
    isEmployee,
    isExistingUser,
    isSelectedEmployment,
    numberOfAdditionalHolidayDays,
    numberOfHolidayDaysContractKeys,
    numberOfHolidayDaysContractState,
    numberOfHolidayDaysPast,
    numberOfHolidayDaysPastKeys,
    numberOfHolidayDaysPastState,
    numberOfHolidayDaysUsed,
    numberOfHolidayDaysUsedKeys,
    numberOfHolidayDaysUsedState,
    readOnly,
    setShowAdditionalDays,
    showAdditionalDays,
    startDate,
    t,
  } = useEmployeeGeneralDataForm();
  const m = (key) => t(key, { ns: 'messages' });
  const tp = (key, opts) =>
    t(`employeeProfile.holidayUi.${key}`, { ns: 'translations', ...opts });
  const calc = calculationData?.calculation;
  const usedLabel =
    endDate?.length > 1
      ? m('numberOfHolidayDaysUsedContract')
      : m('numberOfHolidayDaysUsedYear');

  const daysInput = (keys, placeholder, disabled) => (
    <Form.Item name={keys} noStyle>
      <Input
        min={0}
        type="number"
        size="small"
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => formValidateAndSetNumber(form, e, keys)}
      />
    </Form.Item>
  );

  const year = calc?.holidayDaysYear
    ? Number(String(calc.holidayDaysYear).slice(0, 4))
    : new Date().getFullYear();
  const additional = Number(numberOfAdditionalHolidayDays) || 0;
  // The calculation drops carried-over days once their deadline has passed.
  const pastEffective = Number(calc?.numberOfHolidayDaysPast) || 0;
  const pastEntered = Number(numberOfHolidayDaysPast) || 0;
  const used = Number(numberOfHolidayDaysUsed) || 0;
  const total = calc?.numberOfHolidayDays;
  // A contract ending this year: what counts is what is left until it ends;
  // the year-end figure (a full year's worth) becomes the secondary one.
  const byContractEnd = calc?.numberOfHolidayDaysByEndOfContract;
  const endsThisYear = byContractEnd != null;
  const bottom = endsThisYear ? byContractEnd : total;
  const endDay = endDate ? dayjs(endDate) : null;
  const endLabel = endDay?.isValid() ? endDay.format(DATE_FORMATS.DISPLAY) : '';
  const contractEnded = endsThisYear && endDay?.isBefore(dayjs(), 'day');
  // This year's entitlement as the calculation counts it (the contract
  // figure, or its share up to the contract end / from a start this year), so
  // the lines always add up to the bottom line.
  const entitlement =
    bottom != null ? bottom - additional - pastEffective + used : null;
  const contractDays = Form.useWatch(numberOfHolidayDaysContractKeys, form);
  const isProrated =
    entitlement != null &&
    contractDays != null &&
    Number(contractDays) !== entitlement;

  const canShowPast = isExistingUser && numberOfHolidayDaysPastState?.visible;
  const canShowUsed = isExistingUser && numberOfHolidayDaysUsedState?.visible;

  return (
    <div className="grid gap-4">
      <section>
        <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
          {tp('balanceTitle', { year })}
        </h3>
        <div className="max-w-[560px] rounded-xl border border-zinc-200 bg-white">
          {numberOfHolidayDaysContractState?.visible && (
            <LedgerRow
              sign=""
              label={tp('entitlement', { year })}
              note={
                <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  {endsThisYear
                    ? tp('entitlementToContractEnd', { date: endLabel })
                    : isProrated
                      ? tp('entitlementProrated')
                      : null}
                  <span className="inline-flex items-center gap-1">
                    {tp('perContract')}
                    <span className="inline-block w-14 [&_input]:text-right">
                      {daysInput(
                        numberOfHolidayDaysContractKeys,
                        m('numberOfHolidayDaysContract'),
                        readOnly ||
                          (!startDate &&
                            !endDate &&
                            numberOfHolidayDaysContractState?.disabled)
                      )}
                    </span>
                    {tp('perYear')}
                  </span>
                </span>
              }
            >
              {calculationLoading ? (
                <Spin size="small" />
              ) : (
                (entitlement ?? '—')
              )}
            </LedgerRow>
          )}
          {canShowPast && (
            <LedgerRow
              sign="+"
              label={tp('carriedOver', { year: year - 1 })}
              note={
                pastEntered > pastEffective
                  ? tp('carriedOverExpired', { count: pastEntered })
                  : tp('carriedOverNote')
              }
            >
              {daysInput(
                numberOfHolidayDaysPastKeys,
                m('numberOfHolidayDaysPast'),
                !isSelectedEmployment ||
                  numberOfHolidayDaysUsed ||
                  numberOfHolidayDaysPastState?.disabled
              )}
            </LedgerRow>
          )}
          <LedgerRow sign="+" label={m('additionalHolidayDays')}>
            {additional}
          </LedgerRow>
          {canShowUsed && (
            <LedgerRow sign="−" label={usedLabel}>
              {daysInput(
                numberOfHolidayDaysUsedKeys,
                usedLabel,
                !isSelectedEmployment ||
                  numberOfHolidayDaysPast ||
                  numberOfHolidayDaysUsedState?.disabled
              )}
            </LedgerRow>
          )}
          <LedgerRow
            sign="="
            label={
              endsThisYear
                ? tp('leftContractEndDate', { date: endLabel })
                : tp('leftYearEnd')
            }
            note={
              contractEnded ? tp('contractEndedNote', { date: endLabel }) : null
            }
            total
          >
            {calculationLoading ? <Spin size="small" /> : (bottom ?? '—')}
          </LedgerRow>
        </div>
        {pastEffective > 0 && (
          <p className="mt-1.5 text-[11.5px] text-zinc-500">
            {tp('chargedToYear', { year: year - 1 })}
          </p>
        )}
      </section>

      <section className="grid max-w-[560px] gap-2">
        {endsThisYear && (
          <SideFigure
            label={tp('leftYearEndSecondary')}
            hint={m('totalHolidayDaysLeftYearEnd')}
            value={total}
            loading={calculationLoading}
          />
        )}
        {!contractEnded && (
          <SideFigure
            label={tp('earnedToDate')}
            hint={m('currentlyAvailableHolidayDaysInfo')}
            value={calc?.currentlyAvailableHolidayDays}
            loading={calculationLoading}
          />
        )}
      </section>

      {!isEdited && employeeTrackerTypeState?.visible && (
        <Form.Item
          name={employeeTrackerTypeKeys}
          label={t('typeOfEmployee', { ns: 'forms' })}
        >
          <Radio.Group
            className="grid gap-4 sm:flex [&_label]:items-start [&_label]:justify-start"
            disabled={employeeTrackerTypeState?.disabled}
          >
            {TYPE_OF_EMPLOYEE_OPTIONS.map(({ key, value }) => (
              <Radio
                key={key}
                value={value}
                className="mr-0 w-full rounded border border-zinc-200 bg-white px-2 py-1 hover:border-accent"
              >
                {t(key, { ns: 'forms' })}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      )}

      {isEdited &&
        employeeId &&
        (numberOfAdditionalHolidayDays > 0 || showAdditionalDays ? (
          <div className="grid max-w-[560px] grid-cols-2 gap-2">
            <FieldCard
              icon={<PlusCircleOutlined />}
              title={m('additionalHolidayDays')}
            >
              {/* Saves on its own, so it stays usable in the read-only view. */}
              <ConfigProvider componentDisabled={false}>
                <AdditionalHolidayDaysSection
                  employeeId={employeeId}
                  totalDaysFromServer={numberOfAdditionalHolidayDays}
                />
              </ConfigProvider>
            </FieldCard>
          </div>
        ) : !isEmployee ? (
          <button
            type="button"
            className="w-max text-xs font-semibold text-accent hover:underline"
            onClick={() => setShowAdditionalDays(true)}
          >
            + {m('addAdditionalDays')}
          </button>
        ) : null)}
    </div>
  );
}
